import { DatabaseLayoutType } from '@databases/types/database/view';
import { type ComponentType } from 'react';
import { type MessageDescriptor } from 'react-intl-next';

// @ts-ignore
import annieClaire from './assets/annieClaire.png';
// @ts-ignore
import austinLambert from './assets/austinLambert.png';
// @ts-ignore
import customerVideos from './assets/customerVideos.png';
// @ts-ignore
import documentation from './assets/documentation.png';
// @ts-ignore
import fidelisEjima from './assets/fidelisEjima.png';
// @ts-ignore
import ideaThread from './assets/ideaThread.png';
// @ts-ignore
import marketingContent from './assets/marketingContent.png';
// @ts-ignore
import nscia from './assets/nscia.png';
// @ts-ignore
import oliviaYang from './assets/oliviaYang.png';
// @ts-ignore
import prosperify from './assets/prosperify.png';
// @ts-ignore
import publicForum from './assets/publicForum.png';
// @ts-ignore
import stackies from './assets/stackies.png';
// @ts-ignore
import technicalSupport from './assets/technicalSupport.png';
// @ts-ignore
import thrive4Tomorrow from './assets/thrive4Tomorrow.png';
import { messages as applicationTrackerMessages } from './i18n/applicant-tracker-messages.ts';
import { messages as budgetMessages } from './i18n/budget-messages.ts';
import { messages as campaignTrackerMessages } from './i18n/campaign-tracker-messages.ts';
import { messages as capacityPlanningMessages } from './i18n/capacity-planning-messages.ts';
import { messages as contactLibraryMessages } from './i18n/contact-library-messages.ts';
import { messages as contentCalendarMessages } from './i18n/content-calendar-messages.ts';
import { messages as contentLibraryMessages } from './i18n/content-library-messages.ts';
import { messages as feedbackTrackerMessages } from './i18n/feedback-tracker-messages.ts';
import { messages as meetingNotesMessages } from './i18n/meeting-notes-messages.ts';
import { messages } from './i18n/messages.ts';
import { messages as newHireMessages } from './i18n/new-hire-messages.ts';
import { messages as okrTrackerMessages } from './i18n/okr-tracker-messages.ts';
import { messages as productRequirementsMessages } from './i18n/product-requirements-messages.ts';
import { messages as researchRepositoryMessages } from './i18n/research-repository-messages.ts';
import { messages as simpleDatabaseMessages } from './i18n/simple-database-messages.ts';
import { messages as vendorManagementMessages } from './i18n/vendor-management-messages.ts';
import ApplicantTracker from './previews/ApplicantTracker.tsx';
import Budget from './previews/Budget.tsx';
import CampaignTracker from './previews/CampaignTracker.tsx';
import CapacityPlanning from './previews/CapacityPlanning.tsx';
import ContactLibrary from './previews/ContactLibrary.tsx';
import ContentCalendar from './previews/ContentCalendar.tsx';
import ContentLibrary from './previews/ContentLibrary.tsx';
import EmptyDatabase from './previews/EmptyDatabase.tsx';
import FeedbackTracker from './previews/FeedbackTracker.tsx';
import MeetingNotes from './previews/MeetingNotes.tsx';
import NewHire from './previews/NewHire.tsx';
import OkrTracker from './previews/OkrTracker.tsx';
import ProductRequirements from './previews/ProductRequirements.tsx';
import ResearchRepository from './previews/ResearchRepository.tsx';
import VendorManagement from './previews/VendorManagement.tsx';

export type TranslatableTemplateType = Exclude<TemplateType, 'emptyDefault'>;

export type TranslationsMapType = {
  [key in TranslatableTemplateType]: Record<string, MessageDescriptor>;
};

export const translationsMap: TranslationsMapType = {
  productRequirements: productRequirementsMessages,
  budget: budgetMessages,
  campaignTracker: campaignTrackerMessages,
  newHire: newHireMessages,
  legacyContentLibrary: contentLibraryMessages,
  contentLibrary: contentLibraryMessages,
  researchRepository: researchRepositoryMessages,
  vendorManagement: vendorManagementMessages,
  feedbackTracker: feedbackTrackerMessages,
  meetingNotes: meetingNotesMessages,
  capacityPlanning: capacityPlanningMessages,
  contentCalendar: contentCalendarMessages,
  contactLibrary: contactLibraryMessages,
  applicantTracker: applicationTrackerMessages,
  okrTracker: okrTrackerMessages,
  simpleDatabase: simpleDatabaseMessages
};

export type LegacyUserTemplateType = 'legacyContentLibrary';

export type UserTemplateType =
  | 'contactLibrary'
  | 'emptyDefault'
  | 'contentLibrary'
  | 'productRequirements'
  | 'campaignTracker'
  | 'researchRepository'
  | 'meetingNotes'
  | 'contentCalendar'
  | 'capacityPlanning'
  | 'feedbackTracker'
  | 'okrTracker'
  | 'budget'
  | 'applicantTracker'
  | 'vendorManagement'
  | 'newHire';

/**
 * Non-user facing templates
 */
export type SystemTemplateType = 'simpleDatabase';

export type TemplateType = UserTemplateType | LegacyUserTemplateType | SystemTemplateType;

export type TemplateProps = {
  name: UserTemplateType;
  type: DatabaseLayoutType;
  message: MessageDescriptor;
};

export const templatesRecord: Record<UserTemplateType, TemplateProps> = {
  emptyDefault: {
    name: 'emptyDefault',
    type: DatabaseLayoutType.TABLE,
    message: messages['templates.use-template-empty-database-name']
  },
  contentLibrary: {
    name: 'contentLibrary',
    type: DatabaseLayoutType.GALLERY,
    message: messages['templates.use-template-content-library-name']
  },
  productRequirements: {
    name: 'productRequirements',
    type: DatabaseLayoutType.TABLE,
    message: messages['templates.use-template-product-requirements-name']
  },
  campaignTracker: {
    name: 'campaignTracker',
    type: DatabaseLayoutType.BOARD,
    message: messages['templates.use-template-campaign-tracker-name']
  },
  researchRepository: {
    name: 'researchRepository',
    type: DatabaseLayoutType.TABLE,
    message: messages['templates.use-template-research-repository-name']
  },
  contactLibrary: {
    name: 'contactLibrary',
    type: DatabaseLayoutType.GALLERY,
    message: messages['templates.use-template-contact-library-name']
  },
  meetingNotes: {
    name: 'meetingNotes',
    type: DatabaseLayoutType.TABLE,
    message: messages['templates.use-template-meeting-notes-name']
  },
  contentCalendar: {
    name: 'contentCalendar',
    type: DatabaseLayoutType.BOARD,
    message: messages['templates.use-template-content-calendar-name']
  },
  capacityPlanning: {
    name: 'capacityPlanning',
    type: DatabaseLayoutType.TABLE,
    message: messages['templates.use-template-capacity-planning-name']
  },
  feedbackTracker: {
    name: 'feedbackTracker',
    type: DatabaseLayoutType.TABLE,
    message: messages['templates.use-template-feedback-tracker-name']
  },
  okrTracker: {
    name: 'okrTracker',
    type: DatabaseLayoutType.BOARD,
    message: messages['templates.use-template-okr-tracker-name']
  },
  budget: {
    name: 'budget',
    type: DatabaseLayoutType.TABLE,
    message: messages['templates.use-template-budget-name']
  },
  applicantTracker: {
    name: 'applicantTracker',
    type: DatabaseLayoutType.BOARD,
    message: messages['templates.use-template-applicant-tracker-name']
  },
  vendorManagement: {
    name: 'vendorManagement',
    type: DatabaseLayoutType.GALLERY,
    message: messages['templates.use-template-vendor-management-name']
  },
  newHire: {
    name: 'newHire',
    type: DatabaseLayoutType.BOARD,
    message: messages['templates.use-template-new-hire-name']
  }
};

export const templatePreviews: Record<UserTemplateType, ComponentType> = {
  emptyDefault: EmptyDatabase,
  contentLibrary: ContentLibrary,
  productRequirements: ProductRequirements,
  researchRepository: ResearchRepository,
  contactLibrary: ContactLibrary,
  meetingNotes: MeetingNotes,
  campaignTracker: CampaignTracker,
  contentCalendar: ContentCalendar,
  capacityPlanning: CapacityPlanning,
  feedbackTracker: FeedbackTracker,
  okrTracker: OkrTracker,
  budget: Budget,
  newHire: NewHire,
  applicantTracker: ApplicantTracker,
  vendorManagement: VendorManagement
};

export const PLACEHOLDER_DISPLAY_NAMES: Record<string, string> = {
  'dummy-0': 'Leslie Yup',
  'dummy-1': 'Ron Johnson',
  'dummy-2': 'Wyatt Benson',
  'dummy-3': 'Anne Larkin',
  'dummy-4': 'Jerry Gergith',
  'dummy-5': 'Annie Clare',
  'dummy-6': 'Austin Lambert',
  'dummy-7': 'Olivia Yang',
  'dummy-8': 'Fidelis Ejima',
  'dummy-9': 'Victoria Styles',
  'dummy-10': 'Robi Lopez',
  'dummy-11': 'Brian Lin',
  'dummy-12': 'Florence Garcia',
  'dummy-13': 'Gabriella Bossio',
  'dummy-14': 'Issac Varghese',
  'dummy-15': 'Kayla Parajuli',
  'dummy-16': 'Christine Sanchez',
  'dummy-17': 'Omar Salah'
};

export const TEMPLATE_COVER_IMAGES = {
  'content-library-cover-1': {
    imageSrc: documentation,
    fileName: 'documentation.png'
  },
  'content-library-cover-2': {
    imageSrc: customerVideos,
    fileName: 'customerVideos.png'
  },
  'content-library-cover-3': {
    imageSrc: marketingContent,
    fileName: 'marketingContent.png'
  },
  'content-library-cover-4': {
    imageSrc: technicalSupport,
    fileName: 'technicalSupport.png'
  },
  'content-library-cover-5': {
    imageSrc: publicForum,
    fileName: 'publicForum.png'
  },
  'contact-library-cover-1': {
    imageSrc: annieClaire,
    fileName: 'annieClaire.png'
  },
  'contact-library-cover-2': {
    imageSrc: austinLambert,
    fileName: 'austinLambert.png'
  },
  'contact-library-cover-3': {
    imageSrc: oliviaYang,
    fileName: 'oliviaYang.png'
  },
  'contact-library-cover-4': {
    imageSrc: fidelisEjima,
    fileName: 'fidelisEjima.png'
  },
  'vendor-management-cover-1': {
    imageSrc: ideaThread,
    fileName: 'ideaThread.png'
  },
  'vendor-management-cover-2': {
    imageSrc: prosperify,
    fileName: 'prosperify.png'
  },
  'vendor-management-cover-3': {
    imageSrc: stackies,
    fileName: 'stackies.png'
  },
  'vendor-management-cover-4': {
    imageSrc: thrive4Tomorrow,
    fileName: 'thrive4Tomorrow.png'
  },
  'vendor-management-cover-5': {
    imageSrc: nscia,
    fileName: 'nscia.png'
  }
};

export type TemplateCoverImageKey = keyof typeof TEMPLATE_COVER_IMAGES;
